import React, { useMemo, useState } from 'react';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { Pagination } from 'antd';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

export interface AndIteratorProps extends BaseComponentProps, DataSetConfig {
  /** 渲染函数 */
  renderItem?: (item: any, index: number) => React.ReactNode;
  /** 空状态渲染 */
  renderEmpty?: () => React.ReactNode;
  /** 外部数据 */
  items?: any[];
  /** 最大显示数量 */
  maxCount?: number;
  /** 容器样式 */
  containerStyle?: React.CSSProperties;
  /** 子元素 */
  children?: React.ReactNode;
  
  // 分页配置
  "page-disabled"?: boolean;
  "page-defaultPageSize"?: number;
  "page-pageSizeOptions"?: string[];
  "page-showSizeChanger"?: boolean;
  "page-showQuickJumper"?: boolean;
  "page-simple"?: boolean;
  "page-size"?: 'default' | 'small' | 'large';
  "page-showLessItems"?: boolean;
  /** 与 AndPagination 一致：模板占位 ${total}、${range[0]}、${range[1]}、${range} */
  "page-showTotal"?: string;
}

export const AndIterator: React.FC<AndIteratorProps> = ({
  id, sessionId, develop, dataSet, dataSetField, renderItem, renderEmpty, items: propItems, maxCount, containerStyle, children,
  "page-disabled": pageDisabled = false,
  "page-defaultPageSize": defaultPageSize = 10,
  "page-pageSizeOptions": pageSizeOptions = ['10', '20', '30', '40'],
  "page-showSizeChanger": showSizeChanger = true,
  "page-showQuickJumper": showQuickJumper = false,
  "page-simple": simple = false,
  "page-size": size = 'small',
  "page-showLessItems": showLessItems = false,
  "page-showTotal": pageShowTotalTpl,
}) => {
  const paginationSize = size === 'default' ? 'middle' : size;
  const paginationShowTotal = useMemo(() => {
    const tpl = pageShowTotalTpl?.trim();
    if (!tpl) return undefined;
    return (total: number, range: [number, number]) =>
      tpl
        .replace(/\$\{total\}/g, String(total))
        .replace(/\$\{range\[0\]\}/g, String(range[0]))
        .replace(/\$\{range\[1\]\}/g, String(range[1]))
        .replace(/\$\{range\}/g, `${range[0]}-${range[1]}`);
  }, [pageShowTotalTpl]);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 分页状态
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // 从 store 获取数据
  const storeItems = useMemo(() => {
    if (dataSetKey && dataSetField) {
      const data = getDataSet(dataSetKey, dataSetField);
      return Array.isArray(data) ? data : [];
    }
    return [];
  }, [dataSetKey, dataSetField, getDataSet]);

  // 设计器模式(develop)：优先 store，无则回退到静态 items；运行时且配置了 dataSet：仅用 store，不回退到静态 items
  const allItems = useMemo(() => {
    if (develop) return storeItems.length > 0 ? storeItems : (propItems || []);
    if (dataSetKey) return storeItems;
    return propItems || [];
  }, [develop, dataSetKey, storeItems, propItems]);

  // 计算分页后的数据
  const displayItems = useMemo(() => {
    let list = allItems;
    
    // 如果启用分页且未禁用
    if (!pageDisabled) {
      const start = (current - 1) * pageSize;
      const end = start + pageSize;
      list = list.slice(start, end);
    }

    return maxCount ? list.slice(0, maxCount) : list;
  }, [allItems, pageDisabled, current, pageSize, maxCount]);

  // 空状态
  if (!allItems || allItems.length === 0) {
    if (renderEmpty) return <>{renderEmpty()}</>;
    return <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>暂无数据</div>;
  }

  // 分页变更处理
  const handlePageChange = (page: number, size?: number) => {
    setCurrent(page);
    if (size) setPageSize(size);
  };

  // 渲染列表
  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...containerStyle }}>
      <div style={{ flex: 1 }}>
        {displayItems.map((item, index) => (
          <React.Fragment key={item.id || index}>
            {renderItem ? renderItem(item, index) : (
              <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                {JSON.stringify(item)}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      
      {!pageDisabled && allItems.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={current}
            pageSize={pageSize}
            total={allItems.length}
            onChange={handlePageChange}
            pageSizeOptions={pageSizeOptions}
            showSizeChanger={showSizeChanger}
            showQuickJumper={showQuickJumper}
            simple={simple}
            size={paginationSize}
            showLessItems={showLessItems}
            showTotal={paginationShowTotal}
          />
        </div>
      )}
    </div>
  );
};

export default AndIterator;
